// ─────────────────────────────────────────────
// Oike — Gastos: the rules (pure; the server applies them, the screens show them; tested by scripts/test-expenses.mjs).
// - An expense is what is owed; its payments are the money that went out (same currency, never converted).
// - Paid, partial, pending and overdue are computed, never stored. Inconsistencies (payments above the amount, a
//   payment linked to several expenses, a payment in another currency, an unconfirmed payment, a payment requested but
//   not registered, the same operation with different content) are shown as «requiere revisión» and the balance is not
//   presented as definitive until they are resolved. Nothing is voided or hidden automatically, except records that are
//   provably the same operation (same key and same content), which keep the oldest and say so.
// - A payment declared in another currency is not supported yet: it is kept as a notice and the balance stays open.
// ─────────────────────────────────────────────
import { EXPENSE as E, EXPENSE_PAYMENT as P, EXPENSE_CURRENCIES, RECURRENCES, PAID_FROM, PAYMENT_METHODS } from './expenseSchema.js';

export class ExpenseError extends Error { constructor(code) { super(code); this.code = code; } }
const fail = (code) => { throw new ExpenseError(code); };
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const OP_RE = /^[A-Za-z0-9-]{8,64}$/;
const sel = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name ?? '' : v ?? '');
const links = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'string' ? x : x?.id)).filter(Boolean) : []);
export const cents = (n) => Math.round(Number(n) * 100);
export const money = (c) => Math.round(c) / 100;
const text = (v, max) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
const longText = (v, max) => String(v ?? '').trim().slice(0, max);
const parse = (v, d) => { try { return v ? JSON.parse(v) : d; } catch { return d; } };

export const cleanOp = (op) => (OP_RE.test(String(op || '')) ? String(op) : fail('invalid_op'));
export const expenseKeyOf = (op) => `exp:${op}`;
export const paymentKeyOf = (op) => `pay:${op}`;

function amountOf(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0 || n > 10_000_000) fail('invalid_amount');
  return money(cents(n));
}
function dayOf(v, code, optional = false) {
  if (!v && optional) return '';
  if (!DAY_RE.test(String(v || ''))) fail(code);
  return String(v);
}

/** The expense as written by Ale (validated): never the payment, never the scope. */
export function cleanExpenseInput(input = {}, { categories = [] } = {}) {
  const concept = text(input.concept, 200); if (!concept) fail('missing_concept');
  const supplier = text(input.supplier, 200); if (!supplier) fail('missing_supplier');
  const currency = EXPENSE_CURRENCIES.includes(input.currency) ? input.currency : fail('invalid_currency');
  const category = String(input.category || '');
  if (!categories.some(c => c.key === category && !c.archived)) fail('invalid_category');
  const recurrence = RECURRENCES.includes(input.recurrence || 'none') ? (input.recurrence || 'none') : fail('invalid_recurrence');
  return {
    concept, supplier, amount: amountOf(input.amount), currency, incurredOn: dayOf(input.incurredOn, 'invalid_date'),
    dueOn: dayOf(input.dueOn, 'invalid_due', true), category, recurrence, note: longText(input.note, 2000),
  };
}

/** A payment of an expense: the same currency as the expense (another currency is not supported yet). */
export function cleanPaymentInput(p = {}, { currency }) {
  if (p.currency && p.currency !== currency) fail('other_currency_not_supported');
  return {
    amount: amountOf(p.amount), currency, paidOn: dayOf(p.paidOn, 'invalid_paid_on'),
    paidFrom: PAID_FROM.includes(p.paidFrom) ? p.paidFrom : fail('invalid_paid_from'),
    method: PAYMENT_METHODS.includes(p.method || 'other') ? (p.method || 'other') : fail('invalid_method'),
    reference: text(p.reference, 200),
  };
}

/**
 * How the expense was left in the same form: paid (one payment for the whole amount), partial (a payment below it) or
 * pending; or a payment declared in another currency (kept as a notice; the expense stays open).
 */
export function settleOf(settle = {}, expense) {
  const mode = ['paid', 'partial', 'pending'].includes(settle.mode) ? settle.mode : fail('invalid_mode');
  if (mode === 'pending') return { mode, payment: null, otherCurrency: null };
  if (settle.otherCurrency) {
    const oc = settle.otherCurrency;
    const currency = EXPENSE_CURRENCIES.includes(oc.currency) && oc.currency !== expense.currency ? oc.currency : fail('invalid_other_currency');
    return { mode, payment: null, otherCurrency: { currency, paidOn: dayOf(oc.paidOn, 'invalid_paid_on', true), note: longText(oc.note, 500) } };
  }
  const payment = cleanPaymentInput({ ...settle.payment, amount: mode === 'paid' ? expense.amount : settle.payment?.amount }, { currency: expense.currency });
  if (mode === 'partial' && cents(payment.amount) >= cents(expense.amount)) fail('partial_not_below_amount');
  return { mode, payment, otherCurrency: null };
}

/** The content that makes two records the same operation (with the same key): nothing else is ever reconciled. */
export const expenseContentOf = (f = {}) => [text(f[E.concept], 200).toLowerCase(), text(f[E.supplier], 200).toLowerCase(), cents(f[E.amount]), sel(f[E.currency]), f[E.incurredOn] || ''].join('|');
export const paymentContentOf = (f = {}) => [cents(f[P.amount]), sel(f[P.currency]), f[P.paidOn] || '', sel(f[P.paidFrom]), sel(f[P.method]), links(f[P.expense]).slice().sort().join(',')].join('|');

const older = (a, b) => String(a.createdTime || '').localeCompare(String(b.createdTime || '')) || a.id.localeCompare(b.id);
/**
 * Records found under one operation key: the oldest is kept; the others are duplicates only if they have the same
 * content. Different content under the same key is a conflict for review: nothing is changed.
 */
export function reconcileSameOperation(rows = [], contentOf) {
  const live = rows.slice().sort(older);
  if (live.length <= 1) return { kept: live[0] || null, duplicates: [], conflict: false };
  const kept = live[0];
  const same = live.slice(1).filter(r => contentOf(r.fields || {}) === contentOf(kept.fields || {}));
  return { kept, duplicates: same, conflict: same.length !== live.length - 1 };
}

/** The intent saved with the expense: a payment still to register, or a payment declared in another currency. */
export const intentOf = (f = {}) => parse(f[E.intent], null);

/**
 * The state of one expense from its payments. `allPayments`: every payment of the scope (a payment counts only for
 * the one expense it is linked to). `keyConflicts`: expense ids that share a key with different content.
 */
export function expenseState(expense, allPayments = [], { today = new Date().toISOString().slice(0, 10), keyConflicts = new Set() } = {}) {
  const f = expense?.fields || {};
  const currency = sel(f[E.currency]);
  const amountC = cents(f[E.amount] || 0);
  const mine = allPayments.filter(p => links(p.fields?.[P.expense]).includes(expense.id));
  const review = [];
  let paidC = 0;
  const views = mine.map(p => {
    const pf = p.fields || {};
    const status = sel(pf[P.status]) || 'pending';
    const multi = links(pf[P.expense]).length > 1;
    const otherCur = sel(pf[P.currency]) !== currency;
    if (status === 'confirmed' && !multi && !otherCur) paidC += cents(pf[P.amount]);
    if (status !== 'voided' && multi) review.push('payment_multiple_links');
    if (status !== 'voided' && otherCur) review.push('payment_other_currency');
    if (status === 'pending') review.push('unconfirmed_payment');
    return { id: p.id, amount: Number(pf[P.amount]) || 0, currency: sel(pf[P.currency]), paidOn: pf[P.paidOn] || '', paidFrom: sel(pf[P.paidFrom]), method: sel(pf[P.method]),
      reference: pf[P.reference] || '', status, voidReason: pf[P.voidReason] || '', multi, otherCurrency: otherCur, createdTime: p.createdTime };
  }).sort((a, b) => String(a.paidOn).localeCompare(String(b.paidOn)) || String(a.createdTime).localeCompare(String(b.createdTime)));
  const intent = intentOf(f);
  if (intent?.kind === 'payment') review.push('incomplete_operation');
  if (keyConflicts.has(expense.id)) review.push('same_key_conflict');
  const cancelled = sel(f[E.status]) === 'cancelled';
  if (paidC > amountC) review.push('overpaid');
  const pendingC = Math.max(amountC - paidC, 0);
  const due = f[E.dueOn] || '';
  const reviewUnique = [...new Set(review)];
  // With payments above the amount there is no definitive balance until it is resolved
  const definitive = !reviewUnique.includes('overpaid');
  const state = cancelled ? 'cancelled' : !definitive ? 'review' : pendingC === 0 && amountC > 0 ? 'paid' : paidC > 0 ? 'partial' : due && due < today ? 'overdue' : 'pending';
  return {
    id: expense.id, currency, amount: money(amountC), paid: money(paidC), pending: definitive && !cancelled ? money(pendingC) : null,
    state, overdue: !cancelled && definitive && pendingC > 0 && !!due && due < today, dueOn: due,
    review: cancelled ? [] : reviewUnique, intent: cancelled ? null : intent, payments: views,
  };
}

/** Expense ids that share an operation key with an expense of different content (shown for review). */
export function keyConflictsOf(expenses = []) {
  const byKey = new Map();
  for (const e of expenses) { const k = e.fields?.[E.key]; if (k && sel(e.fields?.[E.status]) !== 'cancelled') byKey.set(k, [...(byKey.get(k) || []), e]); }
  const out = new Set();
  for (const list of byKey.values()) if (list.length > 1 && new Set(list.map(e => expenseContentOf(e.fields))).size > 1) for (const e of list) out.add(e.id);
  return out;
}

// ── Subscriptions: the next period is suggested, never registered by itself ──
export function periodOf(day, recurrence) {
  if (!DAY_RE.test(day || '')) return '';
  return recurrence === 'yearly' ? day.slice(0, 4) : recurrence === 'monthly' ? day.slice(0, 7) : '';
}
export function nextPeriod(period, recurrence) {
  if (recurrence === 'yearly' && /^\d{4}$/.test(period)) return String(Number(period) + 1);
  if (recurrence === 'monthly' && /^\d{4}-\d{2}$/.test(period)) {
    const [y, m] = period.split('-').map(Number);
    return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
  }
  return '';
}
const periodStart = (p) => (/^\d{4}$/.test(p) ? `${p}-01-01` : `${p}-01`);
const addDays = (day, n) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

/** The next period of each series that repeats, once its start is within 10 days and it is not registered yet. */
export function suggestionsOf(expenses = [], { today = new Date().toISOString().slice(0, 10) } = {}) {
  const series = new Map();
  for (const e of expenses) {
    const f = e.fields || {};
    if (sel(f[E.status]) === 'cancelled') continue;
    const key = f[E.seriesKey] || e.id;
    series.set(key, [...(series.get(key) || []), e]);
  }
  const out = [];
  for (const [key, list] of series) {
    const latest = list.slice().sort((a, b) => String(a.fields?.[E.period] || a.fields?.[E.incurredOn]).localeCompare(String(b.fields?.[E.period] || b.fields?.[E.incurredOn]))).pop();
    const f = latest.fields || {};
    const recurrence = sel(f[E.recurrence]);
    if (!['monthly', 'yearly'].includes(recurrence)) continue;
    const period = f[E.period] || periodOf(f[E.incurredOn], recurrence);
    const next = nextPeriod(period, recurrence);
    if (!next || list.some(x => x.fields?.[E.period] === next)) continue;
    if (periodStart(next) > addDays(today, 10)) continue;
    out.push({ seriesKey: key, fromId: latest.id, period: next, recurrence, concept: f[E.concept] || '', supplier: f[E.supplier] || '',
      amount: Number(f[E.amount]) || 0, currency: sel(f[E.currency]), category: f[E.category] || '', incurredOn: periodStart(next) });
  }
  return out.sort((a, b) => a.incurredOn.localeCompare(b.incurredOn));
}

/** For Hoy: open expenses overdue or due within `days`, and operations left incomplete or needing review. */
export function dueItems(list = [], { today = new Date().toISOString().slice(0, 10), days = 7 } = {}) {
  const limit = addDays(today, days);
  return list.filter(x => x.state.state !== 'cancelled' && x.state.state !== 'paid' && (
    (x.state.dueOn && x.state.dueOn <= limit) || x.state.review.includes('incomplete_operation') || x.state.review.includes('unconfirmed_payment')))
    .sort((a, b) => String(a.state.dueOn || '9999').localeCompare(String(b.state.dueOn || '9999')));
}

/** Per currency (never added across currencies): paid in [from, to] (confirmed payments), pending and overdue now. */
export function totalsByCurrency(list = [], { from = '', to = '' } = {}) {
  const out = {};
  const slot = (c) => (out[c] = out[c] || { paidC: 0, pendingC: 0, overdueC: 0, review: 0 });
  for (const x of list) {
    const s = x.state;
    if (s.state === 'cancelled') continue;
    for (const p of s.payments) if (p.status === 'confirmed' && !p.multi && !p.otherCurrency && (!from || p.paidOn >= from) && (!to || p.paidOn <= to)) slot(s.currency).paidC += cents(p.amount);
    if (s.pending !== null) { slot(s.currency).pendingC += cents(s.pending); if (s.overdue) slot(s.currency).overdueC += cents(s.pending); }
    if (s.review.length) slot(s.currency).review += 1;
  }
  return Object.fromEntries(Object.entries(out).map(([c, v]) => [c, { paid: money(v.paidC), pending: money(v.pendingC), overdue: money(v.overdueC), review: v.review }]));
}

export const REVIEW_TEXT = {
  overpaid: 'Los pagos registrados superan el importe del gasto: revisá si el importe o algún pago está mal. Hasta resolverlo no hay saldo definitivo.',
  payment_multiple_links: 'Un pago está vinculado a más de un gasto: revisalo en Airtable. No se cuenta hasta resolverlo.',
  payment_other_currency: 'Un pago tiene otra moneda que el gasto: no se cuenta (los pagos en otra moneda todavía no están soportados).',
  unconfirmed_payment: 'Hay un pago sin confirmar: confirmalo o anulalo si fue un error.',
  incomplete_operation: 'Operación incompleta: falta registrar el pago que indicaste.',
  same_key_conflict: 'Hay otro gasto con la misma operación pero datos distintos: revisá cuál es el correcto (no se cambió nada).',
};
